import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Route, CheckCircle2, Clock, MapPin, Plus, ArrowRight, XCircle } from 'lucide-react';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';

export default function VisitPlanner() {
  const { api, selectedBusiness } = useApp();
  const [visits, setVisits] = useState([]);
  const [salesPoints, setSalesPoints] = useState([]);
  const [users, setUsers] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [userFilter, setUserFilter] = useState('all');

  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });

  const load = useCallback(async () => {
    try {
      const visitParams = { from_date: format(weekStart, 'yyyy-MM-dd'), to_date: format(weekEnd, 'yyyy-MM-dd') };
      const spParams = {};
      if (selectedBusiness) {
        visitParams.business_id = selectedBusiness;
        spParams.business_id = selectedBusiness;
      }
      const [vRes, spRes, uRes] = await Promise.all([
        api.getVisits(visitParams),
        api.getSalesPoints(spParams),
        api.getUsers(),
      ]);
      setVisits(vRes.data);
      setSalesPoints(spRes.data);
      setUsers(uRes.data.data || uRes.data);
    } catch (e) { console.error(e); }
  }, [api, weekStart, weekEnd, selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  const filtered = userFilter === 'all' ? visits : visits.filter(v => v.user_id === userFilter);

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    days.push(d);
  }

  const getSPName = (id) => salesPoints.find(s => s.id === id)?.name || 'Unknown';
  const getSPCity = (id) => salesPoints.find(s => s.id === id)?.city || '';
  const getUserName = (id) => users.find(u => u.id === id)?.name || 'Unknown';

  const statusIcon = (s) => {
    if (s === 'completed') return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
    if (s === 'cancelled') return <XCircle className="w-3.5 h-3.5 text-red-500" />;
    return <Clock className="w-3.5 h-3.5 text-amber-600" />;
  };

  const statusColor = (s) => {
    if (s === 'completed') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (s === 'cancelled') return 'bg-red-100 text-red-700 border-red-200';
    return 'bg-amber-100 text-amber-700 border-amber-200';
  };

  const planned = filtered.filter(v => v.status === 'planned').length;
  const completed = filtered.filter(v => v.status === 'completed').length;
  const total = filtered.length;

  return (
    <div data-testid="visit-planner-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Visit Planner</h1>
          <p className="text-sm text-slate-500 mt-1">
            Week of {format(weekStart, 'MMM dd')} - {format(weekEnd, 'MMM dd, yyyy')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger data-testid="planner-user-filter" className="w-48">
              <SelectValue placeholder="Filter by user" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Team Members</SelectItem>
              {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="kpi-card text-center">
          <p className="text-2xl font-extrabold text-slate-900">{total}</p>
          <p className="text-sm text-slate-500">Total Visits</p>
        </div>
        <div className="kpi-card text-center">
          <p className="text-2xl font-extrabold text-amber-600">{planned}</p>
          <p className="text-sm text-slate-500">Planned</p>
        </div>
        <div className="kpi-card text-center">
          <p className="text-2xl font-extrabold text-emerald-600">{completed}</p>
          <p className="text-sm text-slate-500">Completed</p>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="outline" size="sm" onClick={() => setWeekStart(subWeeks(weekStart, 1))} data-testid="prev-week-btn">
          Previous Week
        </Button>
        <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="current-week-btn">
          Current Week
        </Button>
        <Button variant="outline" size="sm" onClick={() => setWeekStart(addWeeks(weekStart, 1))} data-testid="next-week-btn">
          Next Week
        </Button>
      </div>

      {/* Weekly Grid */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const dayVisits = filtered.filter(v => v.visit_date === dateStr);
          const isToday = format(new Date(), 'yyyy-MM-dd') === dateStr;

          return (
            <Card
              key={dateStr}
              data-testid={`planner-day-${dateStr}`}
              className={`border shadow-sm min-h-[200px] ${isToday ? 'border-amber-400 ring-1 ring-amber-400/30' : 'border-border/60'}`}
            >
              <CardHeader className="p-3 pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs font-bold uppercase tracking-wider ${isToday ? 'text-amber-600' : 'text-slate-400'}`}>
                      {format(day, 'EEE')}
                    </p>
                    <p className={`text-lg font-extrabold ${isToday ? 'text-amber-600' : 'text-slate-900'}`}>
                      {format(day, 'dd')}
                    </p>
                  </div>
                  {dayVisits.length > 0 && (
                    <Badge variant="secondary" className="text-[10px]">{dayVisits.length}</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-2">
                {dayVisits.map(v => (
                  <div key={v.id} data-testid={`planner-visit-${v.id}`} className="p-2 rounded-md border border-border/30 bg-slate-50/50 hover:bg-white transition-colors">
                    <div className="flex items-start gap-1.5">
                      {statusIcon(v.status)}
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-800 truncate">{v.purpose}</p>
                        <p className="text-[10px] text-slate-400 flex items-center gap-0.5 truncate">
                          <MapPin className="w-2.5 h-2.5" />
                          {getSPName(v.sales_point_id)}
                        </p>
                        <p className="text-[10px] text-slate-400 truncate">{getUserName(v.user_id)}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {dayVisits.length === 0 && (
                  <p className="text-[10px] text-slate-300 text-center py-2">No visits</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
